<script>
	// Pick a wholesale customer: the full list (copied nightly from Verifone) with a tab per brand
	// and instant search; a live Verifone search is the fallback for customers not in the list yet.
	import { createEventDispatcher, onMount } from 'svelte';
	import fetchData from '../api.js';
	import UserStore from '../stores/user';
	import Icon from './Icon.svelte';

	// optional button under the list, e.g. "open a cart without a customer for now"
	export let skipLabel = '';

	const dispatch = createEventDispatcher();
	const BRANDS = ['ALDO MORO', 'ELIXIR', 'VIBE'];
	const TAB_KEY = 'agents_customer_tab';

	let all = [];
	let loadingList = true;
	let q = '';
	let tab = '';
	let input;

	// live Verifone search (fallback)
	let live = [];
	let liveSearching = false;
	let liveSearched = false;
	let offline = false;
	let requestNo = 0;

	// the last brand tab is remembered on this tablet
	try { tab = localStorage.getItem(TAB_KEY) || ''; } catch (e) { tab = ''; }
	function saveTab(value) {
		try { localStorage.setItem(TAB_KEY, value); } catch (e) { /* storage blocked */ }
	}
	$: saveTab(tab);

	$: counts = BRANDS.reduce((m, b) => ({ ...m, [b]: all.filter((c) => c.brand === b).length }), {});
	$: noBrand = all.filter((c) => !c.brand).length;
	$: needle = q.trim().toLowerCase();
	$: shown = all.filter((c) =>
		(tab === '' || (tab === '-' ? !c.brand : c.brand === tab)) &&
		(!needle || [c.name, c.customer_no, c.city, c.phone].some((v) => String(v || '').toLowerCase().includes(needle))));

	onMount(async () => {
		if (input) input.focus();
		const res = await fetchData({ action: 'agents_customers_list', token: $UserStore.data });
		all = (res && res.data) || [];
		loadingList = false;
	});

	async function searchVerifone() {
		const mine = ++requestNo;
		liveSearching = true;
		const res = await fetchData({ action: 'agents_search_customers', token: $UserStore.data, q: q.trim() });
		if (mine !== requestNo) return;
		const seen = new Set();
		live = ((res && res.data) || []).filter((c) => !seen.has(c.CustomerNo) && seen.add(c.CustomerNo));
		offline = !!(res && res.offline);
		liveSearching = false;
		liveSearched = true;
	}

	function pick(c) {
		dispatch('select', { CustomerNo: c.customer_no ?? c.CustomerNo, CustomerName: c.name ?? c.CustomerName });
	}

	$: q, (liveSearched = false), (live = []);
</script>

<div class="backdrop over-sheet" on:click={() => dispatch('close')} />
<div class="modal" role="dialog" aria-label="בחירת לקוח">
	<div class="modal-head">
		<h2>בחירת לקוח</h2>
		<button class="icon-btn" on:click={() => dispatch('close')} aria-label="סגירה"><Icon name="close" /></button>
	</div>
	<div class="tools">
		<label class="search">
			<span class="icon"><Icon name="search" size={22} /></span>
			<input bind:this={input} type="search" placeholder="שם לקוח, מספר לקוח, עיר או טלפון" bind:value={q} />
		</label>
		{#if all.length}
			<div class="tabs">
				<button class:on={tab === ''} on:click={() => (tab = '')}>הכל <span>{all.length}</span></button>
				{#each BRANDS as b}
					{#if counts[b]}<button class:on={tab === b} on:click={() => (tab = b)}>{b} <span>{counts[b]}</span></button>{/if}
				{/each}
				{#if noBrand}<button class:on={tab === '-'} on:click={() => (tab = '-')}>ללא מותג <span>{noBrand}</span></button>{/if}
			</div>
		{/if}
	</div>
	<div class="modal-body">
		{#if loadingList}
			<div class="spinner" />
		{:else}
			{#if all.length}
				<div class="list">
					{#each shown as c (c.customer_no)}
						<button class="row" on:click={() => pick(c)}>
							<span class="avatar">{(c.name || '?').trim().charAt(0)}</span>
							<span class="who">
								<b>{c.name}</b>
								<span class="muted">לקוח {c.customer_no}{c.city ? ' · ' + c.city : ''}</span>
							</span>
							{#if c.brand}<span class="brand">{c.brand}</span>{/if}
						</button>
					{/each}
				</div>
				{#if !shown.length}<div class="empty-state"><b>אין לקוח כזה ברשימה</b></div>{/if}
			{/if}

			{#if needle.length >= 2 && (!all.length || !shown.length)}
				<!-- not in the nightly list (e.g. a customer opened today): search Verifone itself -->
				{#if liveSearching}
					<div class="spinner" />
				{:else if !liveSearched}
					<div class="center"><button class="btn-ghost small" on:click={searchVerifone}><Icon name="search" size={18} /> חיפוש בווריפון</button></div>
				{:else}
					{#if offline}<div class="badge-warn offline">ווריפון לא זמין כרגע · מוצגים לקוחות מהזמנות קודמות</div>{/if}
					{#if !live.length}<div class="empty-state"><b>לא נמצאו לקוחות</b>נסה שם אחר או מספר לקוח.</div>{/if}
					<div class="list">
						{#each live as c (c.CustomerNo)}
							<button class="row" on:click={() => pick(c)}>
								<span class="avatar">{(c.CustomerName || '?').trim().charAt(0)}</span>
								<span class="who"><b>{c.CustomerName}</b><span class="muted">לקוח {c.CustomerNo}{c.City ? ' · ' + c.City : ''}</span></span>
							</button>
						{/each}
					</div>
				{/if}
			{/if}
		{/if}
	</div>
	{#if skipLabel}
		<div class="foot"><button class="btn-ghost small" on:click={() => dispatch('skip')}>{skipLabel}</button></div>
	{/if}
</div>

<style>
	.modal { height: min(760px, 88vh); }
	.tools { padding: 14px 20px 0; display: flex; flex-direction: column; gap: 10px; }
	.tabs { display: flex; gap: 6px; overflow-x: auto; padding-bottom: 10px; border-bottom: 1px solid var(--line); }
	.tabs button {
		height: 40px; padding: 0 14px; border-radius: 10px; border: 1px solid var(--line); background: var(--surface);
		font-size: 15px; white-space: nowrap; display: flex; align-items: center; gap: 6px;
	}
	.tabs button span { color: var(--muted); font-size: 13px; }
	.tabs button.on { background: var(--ink); color: #fff; border-color: var(--ink); }
	.tabs button.on span { color: rgba(255, 255, 255, .7); }
	.modal-body { flex: 1; padding-top: 4px; }
	.row {
		display: flex; align-items: center; gap: 14px; width: 100%; min-height: 64px;
		padding: 8px 6px; border: 0; border-bottom: 1px solid var(--line); background: none; text-align: start;
	}
	.row:active { background: var(--bg); }
	.avatar { width: 42px; height: 42px; border-radius: 50%; background: var(--bg); display: grid; place-items: center; font-weight: 800; flex: none; }
	.who { flex: 1; min-width: 0; display: flex; flex-direction: column; }
	.who b { font-size: 17px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	.who .muted { font-size: 14px; }
	.brand { font-size: 12px; font-weight: 700; letter-spacing: .04em; color: var(--muted); border: 1px solid var(--line); border-radius: 6px; padding: 2px 6px; white-space: nowrap; }
	.center { display: flex; justify-content: center; padding: 18px 0; }
	.offline { display: block; margin: 10px 0; white-space: normal; }
	.foot { padding: 12px 20px; border-top: 1px solid var(--line); display: flex; justify-content: center; }
</style>
